import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { DataView } from '../../../components/ui/DataView';
import { Banner } from '../../../components/ui/Banner';
import { cn } from '@repo/utils';
import { useHealthDetails, type HealthDetails } from './health.api';

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 5) return 'Just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function MetricRow({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-text-secondary">{label}</span>
      <span className={cn('text-sm font-mono text-text-primary', danger && 'text-text-danger')}>{value}</span>
    </div>
  );
}

function MetricCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-24" />
      </CardHeader>
      <CardContent>
        <Skeleton rows={3} />
      </CardContent>
    </Card>
  );
}

function StatusBanner({ allOk, lastChecked, onRefresh, refreshing }: {
  allOk: boolean;
  lastChecked: string;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <Banner
      variant={allOk ? 'info' : 'danger'}
      title={allOk ? 'All Systems Operational' : 'Degraded Performance'}
      description={`Last checked ${formatRelativeTime(lastChecked)}`}
      action={{ label: refreshing ? 'Refreshing\u2026' : 'Refresh', onClick: onRefresh }}
    />
  );
}

function MemoryGauge({ used, total, label }: { used: number; total: number; label: string }) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  const color =
    pct > 90 ? 'bg-danger-500' :
    pct > 70 ? 'bg-warning-500' :
    'bg-success-500';
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-text-secondary">{label}</span>
        <span className="text-xs font-mono text-text-primary">
          {formatBytes(used)} / {formatBytes(total)}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-surface-2 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">{title}</h2>
  );
}

function DatabaseCard({ db }: { db: HealthDetails['db'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>PostgreSQL</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow
          label="Status"
          value={db.status === 'ok' ? 'Connected' : 'Error'}
        />
        <MetricRow
          label="Latency"
          value={`${db.latencyMs.toFixed(1)} ms`}
          danger={db.latencyMs > 100}
        />
        <hr className="border-border" />
        <MetricRow label="Pool Active" value={String(db.poolActive)} />
        <MetricRow label="Pool Idle" value={String(db.poolIdle)} />
        <MetricRow label="Pool Waiting" value={String(db.poolWaiting)} />
        <MetricRow label="Pool Total" value={String(db.poolTotal)} />
      </CardContent>
    </Card>
  );
}

function RedisCard({ redis }: { redis: HealthDetails['redis'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Redis</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow
          label="Status"
          value={redis.status === 'ok' ? 'Connected' : 'Error'}
        />
        <MetricRow
          label="Latency"
          value={`${redis.latencyMs.toFixed(1)} ms`}
          danger={redis.latencyMs > 100}
        />
        <hr className="border-border" />
        <MetricRow label="Server Uptime" value={formatUptime(redis.uptimeSeconds)} />
        <MetricRow label="Version" value={redis.version || 'N/A'} />
        <MetricRow label="Used Memory" value={formatBytes(redis.usedMemoryBytes)} />
        <MetricRow label="Peak Memory" value={formatBytes(redis.peakMemoryBytes)} />
        <MetricRow label="Connections" value={String(redis.totalConnections)} />
      </CardContent>
    </Card>
  );
}

function ApiNodeCard({ api }: { api: HealthDetails['api'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>API / Node.js</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow label="API Status" value={api.status === 'ok' ? 'Healthy' : 'Degraded'} />
        <MetricRow label="Uptime" value={formatUptime(api.uptimeSeconds)} />
        <MetricRow label="API Version" value={api.version} />
        <MetricRow label="Node.js" value={api.nodeVersion} />
        <MetricRow label="Platform" value={`${api.platform} (${api.arch})`} />
        <MetricRow label="CPUs" value={String(api.cpus)} />
        <MetricRow
          label="Load Avg"
          value={api.loadAvg.map((n) => n.toFixed(2)).join(' / ')}
          danger={api.loadAvg[0] > api.cpus}
        />
        <MetricRow label="Process ID" value={String(api.processId)} />
      </CardContent>
    </Card>
  );
}

function BullMqCard({ queues }: { queues: HealthDetails['bullMq'] }) {
  const totalFailed = queues.reduce((s, q) => s + Math.max(0, q.failed), 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span>BullMQ Queues</span>
          {totalFailed > 0 && (
            <StatusBadge status="danger" label={`${totalFailed} failed`} className="ml-2" />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 font-medium text-text-secondary text-xs uppercase tracking-wider">Queue</th>
                <th className="text-right px-4 py-2 font-medium text-text-secondary text-xs uppercase tracking-wider">Waiting</th>
                <th className="text-right px-4 py-2 font-medium text-text-secondary text-xs uppercase tracking-wider">Active</th>
                <th className="text-right px-4 py-2 font-medium text-text-secondary text-xs uppercase tracking-wider">Delayed</th>
                <th className="text-right px-4 py-2 font-medium text-text-secondary text-xs uppercase tracking-wider">Completed</th>
                <th className="text-right px-4 py-2 font-medium text-text-secondary text-xs uppercase tracking-wider">Failed</th>
              </tr>
            </thead>
            <tbody>
              {queues.map((q) => {
                const isUnavailable = q.waiting === -1;
                return (
                  <tr key={q.name} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-2.5 font-medium text-text-primary">
                      <span className="flex items-center gap-2">
                        {q.name}
                        {isUnavailable && <StatusBadge status="danger" label="unreachable" />}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-text-secondary">
                      {isUnavailable ? '\u2014' : q.waiting > 0 ? <span className="text-text-warning">{q.waiting}</span> : q.waiting}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-text-secondary">
                      {isUnavailable ? '\u2014' : q.active > 0 ? <span className="text-text-warning">{q.active}</span> : q.active}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-text-secondary">
                      {isUnavailable ? '\u2014' : q.delayed > 0 ? <span className="text-text-warning">{q.delayed}</span> : q.delayed}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-text-secondary">
                      {isUnavailable ? '\u2014' : q.completed}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-text-danger">
                      {isUnavailable ? '\u2014' : q.failed > 0 ? q.failed : q.failed}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function MemoryCard({ system }: { system: HealthDetails['system'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Memory Usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <MemoryGauge used={system.heapUsed} total={system.heapTotal} label="Heap" />
        <MemoryGauge used={system.rss} total={system.heapTotal} label="RSS" />
        <MetricRow label="Heap Used" value={formatBytes(system.heapUsed)} />
        <MetricRow label="Heap Total" value={formatBytes(system.heapTotal)} />
        <MetricRow label="RSS" value={formatBytes(system.rss)} />
      </CardContent>
    </Card>
  );
}

function EnvCard({ env: e }: { env: HealthDetails['env'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Environment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow
          label="NODE_ENV"
          value={e.nodeEnv}
        />
        <MetricRow label="Deployment" value={e.deploymentMode} />
        <MetricRow label="Storage Driver" value={e.storageDriver} />
        <MetricRow label="Modules" value={e.modules.length > 0 ? `${e.modules.length} loaded` : 'None'} />
        <div className="flex flex-wrap gap-1.5 mt-1">
          {e.modules.map((m) => (
            <span key={m} className="px-1.5 py-0.5 rounded text-caption font-mono bg-surface-2 text-text-secondary">
              {m}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ExternalServicesCard({ sentry, externalServices }: {
  sentry: HealthDetails['sentry'];
  externalServices: HealthDetails['externalServices'];
}) {
  const allSvcs = {
    sentry: { configured: sentry.configured },
    ...externalServices,
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Integrations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {Object.entries(allSvcs).map(([name, svc]) => (
          <div key={name} className="flex items-center justify-between">
            <span className="text-sm capitalize text-text-secondary">{name}</span>
            <StatusBadge
              status={svc.configured ? 'success' : 'info'}
              label={svc.configured ? 'Configured' : 'Not Set'}
              dot={false}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function StorageCard({ storage }: { storage: HealthDetails['storage'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Storage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow label="Driver" value={storage.driver} />
        <MetricRow
          label="Status"
          value={storage.status === 'ok' ? 'Available' : 'Unavailable'}
        />
        <MetricRow
          label="Writable"
          value={storage.writable ? 'Yes' : 'No'}
        />
        <MetricRow label="Path" value={storage.path} />
      </CardContent>
    </Card>
  );
}

function LicenseCard({ license }: { license: HealthDetails['license'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>License</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow
          label="Status"
          value={license.valid ? 'Valid' : license.error || 'Invalid'}
        />
        {license.licensee && <MetricRow label="Licensee" value={license.licensee} />}
        {license.expiresAt && (
          <MetricRow
            label="Expires"
            value={new Date(license.expiresAt).toLocaleDateString()}
            danger={new Date(license.expiresAt) < new Date()}
          />
        )}
        {license.modules && license.modules.length > 0 && (
          <>
            <hr className="border-border" />
            <div className="flex flex-wrap gap-1.5">
              {license.modules.map((m) => (
                <span key={m} className="px-1.5 py-0.5 rounded text-caption font-mono bg-surface-2 text-text-secondary">
                  {m}
                </span>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function GitCard({ git }: { git: HealthDetails['git'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Git</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetricRow label="Branch" value={git.branch} />
        <div>
          <span className="text-sm text-text-secondary block mb-1">Commit</span>
          <code className="text-xs font-mono bg-surface-2 px-1.5 py-0.5 rounded text-text-primary break-all">
            {git.commitSha}
          </code>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardLoadingState() {
  return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-16 w-full" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }, (_, i) => <MetricCardSkeleton key={i} />)}
      </div>
    </div>
  );
}

export function HealthDashboard() {
  const query = useHealthDetails();
  const { isFetching } = query;

  return (
    <DataView query={query} loadingSkeleton={<DashboardLoadingState />}>
      {(data) => (
        <div className="p-6 space-y-6">
          <StatusBanner
            allOk={data.allOk}
            lastChecked={data.system.lastChecked}
            onRefresh={() => query.refetch()}
            refreshing={isFetching}
          />

          {/* Row 1: Infrastructure Services */}
          <SectionHeading title="Infrastructure" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DatabaseCard db={data.db} />
            <RedisCard redis={data.redis} />
            <ApiNodeCard api={data.api} />
          </div>

          {/* Row 2: BullMQ */}
          <SectionHeading title="Message Queues" />
          <BullMqCard queues={data.bullMq} />

          {/* Row 3: Systems & Integrations */}
          <SectionHeading title="Systems & Integrations" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MemoryCard system={data.system} />
            <ExternalServicesCard sentry={data.sentry} externalServices={data.externalServices} />
          </div>

          {/* Row 4: Configuration & Security */}
          <SectionHeading title="Configuration & Security" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <EnvCard env={data.env} />
            <StorageCard storage={data.storage} />
            <div className="space-y-6">
              <LicenseCard license={data.license} />
              <GitCard git={data.git} />
            </div>
          </div>

          <p className="text-xs text-text-muted text-right">
            Last updated {formatRelativeTime(data.system.lastChecked)}
          </p>
        </div>
      )}
    </DataView>
  );
}
